// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';

import {
  spacing,
  mediaMax,
  borderRadius,
  fontSize,
  fontWeight,
} from 'presentations/views/styles';
import { gray01, gray04 } from 'presentations/views/styles/color';
import { TeamListItem } from 'presentations/api/schema/team';
import { transformImageUrl } from 'presentations/assets';

import Headline from 'presentations/views/components/atoms/Headline';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Button from 'presentations/views/components/atoms/Button';
import TeamNewForm from 'presentations/views/components/organisms/TeamNewForm';

export interface Props {
  isLoading?: boolean;
  isSubmitLoading?: boolean;
  teams: TeamListItem[];
  onClickJoinTeam: (team: TeamListItem) => void;
  teamName: string | null;
  isCheck: boolean | null;
  onSubmit: () => void;
  onChange: (e: SyntheticEvent<HTMLInputElement>) => void;
  viewerDomain: string | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  return (
    <Container>
      <HeadlineContainer>
        <Headline variant="h2">{t('headline')}</Headline>
      </HeadlineContainer>
      <Description>
        {t(props.teams.length === 0 ? 'input description' : 'description')}
      </Description>
      <TeamNewForm
        isSubmitLoading={props.isSubmitLoading}
        teamName={props.teamName}
        isCheck={props.isCheck}
        onSubmit={props.onSubmit}
        onChange={props.onChange}
        viewerDomain={props.viewerDomain}
      />
      {props.teams.length > 0 && (
        <>
          <Or>{t('or')}</Or>
          <SelectTeamContainer>
            <SelectTeamTitle variant="h3">
              {t('Teams that can join')}
            </SelectTeamTitle>
            {props.teams.map((team) => (
              <Team>
                <TeamHeader>
                  <TeamAvatar
                    src={transformImageUrl(team.profileImage, {
                      width: 24,
                      height: 24,
                      fill: 'crop',
                    })}
                    alt={team.name || ''}
                  />
                  <TeamName>{team.name}</TeamName>
                  <JoinButton
                    color="primary"
                    onClick={() => props.onClickJoinTeam(team)}
                    isDisabled={props.isSubmitLoading}
                  >
                    {t('Join')}
                  </JoinButton>
                </TeamHeader>
                <TeamDomain>
                  {t('domain description', {
                    domain: '@' + (props.viewerDomain || ''),
                  })}
                </TeamDomain>
              </Team>
            ))}
          </SelectTeamContainer>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${spacing(96)};
`;
const HeadlineContainer = styled.div`
  margin-top: ${spacing(88)};
  margin-bottom: ${spacing(32)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(48)};
  }
`;
const Description = styled.div`
  margin-bottom: ${spacing(24)};
  width: 400px;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;

const SelectTeamContainer = styled.div`
  width: 400px;
  padding: ${spacing(24)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
`;
const SelectTeamTitle = styled(Headline)`
  padding-bottom: ${spacing(24)};
  border-bottom: 1px solid ${gray04};
  font-size: ${fontSize(16)};
`;
const Team = styled.div`
  padding: ${spacing(16)} 0;
  border-bottom: 1px solid ${gray04};

  &:last-of-type {
    padding-bottom: 0;
    border-bottom: none;
  }
`;
const TeamHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${spacing(12)};
`;
const TeamAvatar = styled(Avatar)`
  width: 24px;
  height: 24px;
  margin-right: ${spacing(8)};
`;
const TeamName = styled.div`
  flex-grow: 1;
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
const JoinButton = styled(Button)`
  flex-shrink: 0;
`;
const TeamDomain = styled.div`
  color: ${gray01};
`;

const Or = styled.div`
  margin-bottom: ${spacing(24)};
`;
